import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, first, map, switchMap } from 'rxjs/operators';

import { ApiCardinalService } from '@api/_api/api-payment/api-cardinal.service';
import { PaymentMethodSessionTypes } from '@api/_api/api-payment/api-payment.model';
import { BrowserOnly } from '@core/data-providers/platform/platform.decorators';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { getXConfig } from '@core/infrastructure/state/core.selectors';
import { sendObservabilityLog } from '@shared/analytics/ITXObservabilityCore/helper';

@Injectable()
export class CardinalService {
    public listener: any;

    constructor(
        private readonly apiCardinalService: ApiCardinalService,
        private readonly store: Store,
        @Inject(DOCUMENT) private readonly document: Document,
        @Inject(WINDOW) private readonly window: CommonWindow,
    ) {}

    public isCardinalMethod(method): boolean {
        let response = false;
        // eslint-disable-next-line eqeqeq
        if (method && method.sessionType == PaymentMethodSessionTypes.CARDINAL) {
            response = true;
        }
        return response;
    }

    @BrowserOnly<CardinalService, Observable<boolean>>(of(false))
    public createCardinalSession(storeId: string, languageId: string, orderId: string, paymentMethod): Observable<boolean> {
        const payload = {
            paymentCode: paymentMethod.code,
        };

        return this.apiCardinalService
            .cardinalSession(storeId, languageId, orderId, payload)
            .pipe(
                map((response) => {
                    const payload1: any = { ...response };
                    if (response?.JWT && response.deviceDataCollectionUrl) {
                        payload1.BIN = paymentMethod.BIN ? paymentMethod.BIN : false;
                    }
                    return payload1;
                }),
                switchMap((payload2: any) => {
                    return this.processCardinal(payload2.JWT, payload2.deviceDataCollectionUrl, payload2.BIN)
                        .pipe(
                            map(() => true),
                            catchError((_error: unknown) => of(false))
                        );
                })
            );
    }

    @BrowserOnly<CardinalService, undefined>(undefined)
    public insertIframeCardinal(id: string, jwt: string, url: string): void {
        const form = `
        <form id="collectionForm" name="devicedata" method="POST" action="${url}">
        <input type="hidden" name="JWT" value="${jwt}" />
        </form>
        <script>
        window.onload = function() {
            // Auto submit form on page load
            document.getElementById('collectionForm').submit();
        }
        </script>`;

        const iframe: any = this.document.createElement('iframe');
        iframe.id = id;
        iframe.src = 'data:text/html;charset=utf-8,' + encodeURI(form);
        iframe.height = '1px';
        iframe.width = '1px';
        iframe.style = 'display:none;';

        this.document.body.appendChild(iframe);
    }

    @BrowserOnly<CardinalService, undefined>(undefined)
    public insertIframeWallet(jwt: string, url: string, bin: string): void {
        const form = `
        <form id="collectionForm" name="devicedata" method="POST" action="${url}">
        <input type="hidden" name="Bin" value="${bin}" />
        <input type="hidden" name="JWT" value="${jwt}" />
        </form>
        <script>
        window.onload = function() {
            // Auto submit form on page load
            document.getElementById('collectionForm').submit();
        }
        </script>`;

        const iframe: any = this.document.createElement('iframe');
        iframe.src = 'data:text/html;charset=utf-8,' + encodeURI(form);
        iframe.height = '1px';
        iframe.width = '1px';
        iframe.style = 'display:none;';

        this.document.body.appendChild(iframe);
    }

    @BrowserOnly<CardinalService, undefined>(undefined)
    public iframeChallenge(JWT: string, url: string): void {
        const form = `
        <form id="step-up-form" name="devicedata" method="POST" action="${url}">
        <input type="hidden" name="JWT" value="${JWT}" />
        </form>
        <script>
        window.onload = function() {
            // Auto submit form on page load
            document.getElementById('step-up-form').submit();
        }
        </script>`;
        const div = this.document.createElement('div');
        div.classList.add('psd2-iframe-challenge');

        const iframe = this.document.createElement('iframe');
        iframe.src = 'data:text/html;charset=utf-8,' + encodeURI(form);

        div.appendChild(iframe);
        this.document.body.appendChild(div);
    }

    @BrowserOnly<CardinalService, undefined>(undefined)
    public removeiFrameChallenge(): void {
        const iframes = this.document.getElementsByClassName('psd2-iframe-challenge');
        if (iframes.length === 1) {
            const iframe = iframes[0];
            iframe.remove();
        }
    }

    @BrowserOnly<CardinalService, Observable<undefined>>(of(undefined))
    public processCardinal(jwt: string, url: string, bin: string): Observable<any> {
        return this.getTimeout().pipe(
            first(),
            switchMap((timeout) => {
                return new Observable((observer) => {
                    let timer = null;

                    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
                    function checkout() {
                        window.removeEventListener('message', cardinalListener);

                        if (timer) {
                            clearTimeout(timer);
                            timer = null;
                        }

                        observer.next();
                        observer.complete();
                    }

                    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
                    function cardinalListener(event) {
                        // This domain changes with every environment. This origin value will be the CentinelAPI domain
                        // event message sample
                        // {MessageType: "profile.completed", SessionId: "0_57f063fd-659a-4779-b45b-9e456fdb7935", Status: true}
                        if (url && event.origin && url.includes(event.origin)) {
                            const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
                            // console.log('Merchant received a message:', data);

                            if (data?.Status) {
                                checkout();
                            }
                        }
                    }

                    this.window.addEventListener('message', cardinalListener);

                    if (bin) {
                        this.insertIframeWallet(jwt, url, bin);
                    } else {
                        this.insertIframeCardinal('cardinal-iframe', jwt, url);
                    }
                    // iniciar temporizador por si hay error de Cardinal:
                    timer = setTimeout(() => {
                        checkout();
                    }, timeout);
                });
            })
        );
    }

    @BrowserOnly<CardinalService, Observable<undefined>>(of(undefined))
    public addStep2Listener(onChallengeCallback): void {
        this.listener = function (event) {
            // comprobar origen del mensaje
            if (event.data?.fromChallenge) {
                // if (event.data && event.data.fromChallenge && event.origin === window.origin) {
                // en entornos previos es posible que haya que quitar esta comprobación
                // eliminar listener una vez se ejecuta
                if (event.data.response) {
                    // si hay response
                    let response;
                    try {
                        const responseTxt = atob(event.data.response); // decodificar respuesta en base64
                        response = JSON.parse(responseTxt); // intentar parsear JSON
                    } catch (e) {
                        sendObservabilityLog(e, 'CardinalService addStep2Listener');
                        response = null;
                    }
                    if (response && response.transStatus === 'N') {
                        // si se indica que el usuario no ha validado la operación con el banco
                        // indicar cancelación
                        this.removeiFrameChallenge();
                    }
                }
                this.window.removeEventListener('message', this.listener);
                // en cualquier otro caso (no response, response mal formada, response JSON pero valor indeterminado de transStatus ...)
                onChallengeCallback(event.data.transactionId);
                // invocamos al callback devuelto por el primer asynccheckout para que se envíe con el transactionId
                this.removeiFrameChallenge();
            }
        };

        this.window.addEventListener('message', this.listener, false);
    }

    private getTimeout(): Observable<number> {
        const DEFAULT_TIMEOUT = 30000;

        return this.store.select(getXConfig).pipe(
            first(),
            map((xConfigurations) => {
                return parseInt(xConfigurations.PSD2_RESPONSE_TIMEOUT as string) ?? DEFAULT_TIMEOUT;
            })
        );
    }
}
